import React, { useEffect, useRef, useState, useLayoutEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { Button } from '../ui/button';
import {
  MoreHorizontal, Edit, Key, Wallet, UserCheck, Ban, Trash2, FileImage, Users,
} from 'lucide-react';

/**
 * Renders the action menu via createPortal so it escapes the table's
 * overflow-x-auto clipping context on mobile. Positioned with `fixed` based
 * on the trigger button's bounding rect; flips up/left when there isn't
 * room below/right of the trigger.
 */
export function UserActionsDropdown({
  user, isOpen, onToggle, onEdit, onPassword, onWallet, onBan, onUnban, onDelete, onViewDocs, onViewDownlines,
}) {
  const triggerRef = useRef(null);
  const [coords, setCoords] = useState({ top: 0, left: 0, openUp: false });

  const recompute = useCallback(() => {
    const btn = triggerRef.current;
    if (!btn) return;
    const rect = btn.getBoundingClientRect();
    const menuW = 192;   // w-48
    const menuH = 280;   // approx max menu height; flips if overflow below
    const viewportH = window.innerHeight;
    const viewportW = window.innerWidth;
    const spaceBelow = viewportH - rect.bottom;
    const openUp = spaceBelow < menuH && rect.top > menuH;
    // Right-align to the trigger; clamp inside viewport with 8px gutter.
    let left = rect.right - menuW;
    if (left < 8) left = 8;
    if (left + menuW > viewportW - 8) left = viewportW - menuW - 8;
    const top = openUp ? rect.top - 4 - menuH : rect.bottom + 4;
    setCoords({ top, left, openUp });
  }, []);

  useLayoutEffect(() => {
    if (isOpen) recompute();
  }, [isOpen, recompute]);

  useEffect(() => {
    if (!isOpen) return undefined;
    const close = () => onToggle(null);
    // Close on scroll / resize so the menu never drifts away from the trigger.
    window.addEventListener('scroll', close, true);
    window.addEventListener('resize', close);
    return () => {
      window.removeEventListener('scroll', close, true);
      window.removeEventListener('resize', close);
    };
  }, [isOpen, onToggle]);

  const itemCls = 'w-full px-3 py-2 text-left text-sm flex items-center gap-2';
  const neutralCls = `${itemCls} text-[#A0B4CC] hover:bg-white/10 hover:text-white`;

  return (
    <>
      <Button
        ref={triggerRef}
        size="sm" variant="ghost"
        className="text-[#A0B4CC] hover:text-white"
        onClick={() => onToggle(user.id)}
        data-testid={`user-actions-${user.id}`}
      >
        <MoreHorizontal className="w-4 h-4" />
      </Button>
      {isOpen && createPortal(
        <div
          style={{ position: 'fixed', top: coords.top, left: coords.left, width: 192, zIndex: 60 }}
          className="bg-[#041A3B] border border-white/15 rounded-lg shadow-xl py-1"
          data-testid="user-actions-menu"
          role="menu"
        >
          <button onClick={() => onEdit(user)} className={neutralCls} data-testid="action-edit">
            <Edit className="w-3.5 h-3.5" /> Edit Details
          </button>
          <button onClick={() => onPassword(user)} className={neutralCls} data-testid="action-password">
            <Key className="w-3.5 h-3.5" /> Change Password
          </button>
          <button onClick={() => onWallet(user)} className={neutralCls} data-testid="action-wallet">
            <Wallet className="w-3.5 h-3.5" /> Adjust Wallet
          </button>
          {onViewDocs && (
            <button onClick={() => onViewDocs(user)} className={neutralCls} data-testid="action-view-docs">
              <FileImage className="w-3.5 h-3.5" /> View Documents
            </button>
          )}
          {onViewDownlines && (
            <button onClick={() => onViewDownlines(user)} className={neutralCls} data-testid="action-view-downlines">
              <Users className="w-3.5 h-3.5" /> View Downlines
            </button>
          )}
          <div className="border-t border-white/10 my-1" />
          {user.is_banned ? (
            <button onClick={() => onUnban(user)} className={`${itemCls} text-[#00E676] hover:bg-[#00E676]/10`} data-testid="action-unban">
              <UserCheck className="w-3.5 h-3.5" /> Unban User
            </button>
          ) : (
            <button onClick={() => onBan(user)} className={`${itemCls} text-[#FFEA00] hover:bg-[#FFEA00]/10`} data-testid="action-ban">
              <Ban className="w-3.5 h-3.5" /> Ban User
            </button>
          )}
          {user.role !== 'admin' && (
            <button onClick={() => onDelete(user)} className={`${itemCls} text-[#FF1744] hover:bg-[#FF1744]/10`} data-testid="action-delete">
              <Trash2 className="w-3.5 h-3.5" /> Delete User
            </button>
          )}
        </div>,
        document.body,
      )}
    </>
  );
}
